import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Alert } from 'react-bootstrap';
import { Link, Search, User, X, Check, Briefcase } from 'lucide-react';
import axios from '../../../services/AxiosInstance';
import { getApiUrl } from '../../../utils/ApiUtils';

const LinkEmployeeModal = ({ show, handleClose, user, companyId, onLinked }) => {
    const [employees, setEmployees] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (show && companyId) {
            setError('');
            fetchUnlinkedEmployees();
        }
        if (!show) {
            setSearchTerm('');
            setSelectedEmployee(null);
            setEmployees([]);
            setError('');
        }
    }, [show, companyId]);

    const fetchUnlinkedEmployees = async () => {
        setIsFetching(true);
        try {
            // Fetch both employees and existing mappings to filter out already-linked ones
            const [empRes, mapRes] = await Promise.all([
                axios.get(getApiUrl(`${companyId}/hr/employees`), { withCredentials: true }),
                axios.get(getApiUrl(`${companyId}/hr/user-employee-mappings`), { withCredentials: true })
            ]);
            const allEmployees = empRes.data?.data?.activeEmployees || [];
            const mappings = mapRes.data?.data || [];
            const mappedEmployeeIds = new Set(mappings.map(m => m.employee_id));
            setEmployees(allEmployees.filter(emp => !mappedEmployeeIds.has(emp.employee_id)));
        } catch (error) {
            console.error('Error fetching employees:', error);
        } finally {
            setIsFetching(false);
        }
    };

    const filteredEmployees = employees.filter(emp => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            emp.firstname?.toLowerCase().includes(term) ||
            emp.lastname?.toLowerCase().includes(term) ||
            emp.email?.toLowerCase().includes(term)
        );
    });

    const handleSubmit = async () => {
        if (!selectedEmployee || !user) return;

        setIsLoading(true);
        setError('');
        try {
            const url = getApiUrl(`${companyId}/hr/user-employee-mappings`);
            await axios.post(url, {
                globalUserId: user.user_id,
                employeeId: selectedEmployee.employee_id
            }, { withCredentials: true });
            onLinked();
            handleClose();
        } catch (err) {
            const message = err.response?.data?.message || 'Kunde inte koppla användaren till den anställde';
            setError(message);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <style>{`
                .link-employee-modal .employee-list {
                    max-height: 320px;
                    overflow-y: auto;
                    margin-top: 1rem;
                }

                .link-employee-modal .employee-option {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 0.75rem 1rem;
                    border: 2px solid #e2e8f0;
                    border-radius: 8px;
                    margin-bottom: 0.5rem;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    background: white;
                }

                .link-employee-modal .employee-option:hover {
                    border-color: var(--primary);
                    background: #f8fafc;
                }

                .link-employee-modal .employee-option.selected {
                    border-color: var(--primary);
                    background: var(--rgba-primary-1);
                }

                .link-employee-modal .employee-option .emp-avatar {
                    width: 36px;
                    height: 36px;
                    border-radius: 50%;
                    background: #e2e8f0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 600;
                    font-size: 0.75rem;
                    color: #475569;
                    flex-shrink: 0;
                }

                .link-employee-modal .employee-option.selected .emp-avatar {
                    background: var(--primary);
                    color: white;
                }

                .link-employee-modal .emp-details {
                    flex: 1;
                    min-width: 0;
                }

                .link-employee-modal .emp-name {
                    font-weight: 600;
                    font-size: 0.875rem;
                    color: #1e293b;
                }

                .link-employee-modal .emp-email {
                    font-size: 0.75rem;
                    color: #64748b;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }

                .link-employee-modal .emp-check {
                    flex-shrink: 0;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    border: 2px solid #cbd5e1;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .link-employee-modal .employee-option.selected .emp-check {
                    background: var(--primary);
                    border-color: var(--primary);
                    color: white;
                }

                .link-employee-modal .link-summary {
                    background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
                    border: 1px solid #10b981;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-top: 1rem;
                }

                .link-employee-modal .link-summary-title {
                    font-weight: 600;
                    color: #059669;
                    font-size: 0.875rem;
                    margin-bottom: 0.25rem;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .link-employee-modal .link-summary-content {
                    color: #065f46;
                    font-size: 0.75rem;
                }
            `}</style>

            <Modal show={show} onHide={handleClose} className="modern-modal link-employee-modal" centered>
                <Modal.Header>
                    <Modal.Title>
                        <Link size={24} />
                        Koppla till anställd
                    </Modal.Title>
                    <Button
                        variant="link"
                        className="btn-close p-0"
                        onClick={handleClose}
                        aria-label="Stäng"
                    >
                        <X size={24} />
                    </Button>
                </Modal.Header>

                <Modal.Body>
                    {user && (
                        <Alert variant="info" className="mb-3">
                            <small>
                                Koppla användare <strong>{user.first_name} {user.last_name}</strong> ({user.email}) till en anställd i personalregistret.
                            </small>
                        </Alert>
                    )}

                    <div className="form-group-modern">
                        <label className="form-label-modern">
                            <Search size={16} />
                            Sök anställd
                        </label>
                        <Form.Control
                            type="text"
                            placeholder="Sök på namn eller e-post..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="form-control-modern"
                        />
                    </div>

                    <div className="employee-list">
                        {isFetching ? (
                            <div className="text-center py-4">
                                <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Laddar...</span>
                                </div>
                                <p className="mt-2 text-muted small">Hämtar anställda...</p>
                            </div>
                        ) : filteredEmployees.length > 0 ? (
                            filteredEmployees.map((emp) => (
                                <div
                                    key={emp.employee_id}
                                    className={`employee-option ${selectedEmployee?.employee_id === emp.employee_id ? 'selected' : ''}`}
                                    onClick={() => setSelectedEmployee(emp)}
                                >
                                    <div className="emp-avatar">
                                        {emp.firstname?.charAt(0)}{emp.lastname?.charAt(0)}
                                    </div>
                                    <div className="emp-details">
                                        <div className="emp-name">{emp.firstname} {emp.lastname}</div>
                                        {emp.email && <div className="emp-email">{emp.email}</div>}
                                    </div>
                                    <div className="emp-check">
                                        {selectedEmployee?.employee_id === emp.employee_id && (
                                            <Check size={12} />
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-4">
                                <User size={32} className="text-muted mb-2" />
                                <p className="text-muted small">
                                    {searchTerm ? 'Inga anställda matchar sökningen' : 'Inga anställda hittades'}
                                </p>
                            </div>
                        )}
                    </div>

                    {error && (
                        <Alert variant="danger" className="mt-2 mb-2">
                            <small>{error}</small>
                        </Alert>
                    )}

                    {selectedEmployee && (
                        <div className="link-summary">
                            <div className="link-summary-title">
                                <Check size={16} />
                                Redo att koppla
                            </div>
                            <div className="link-summary-content">
                                <strong>{user?.first_name} {user?.last_name}</strong> kopplas till anställd <strong>{selectedEmployee.firstname} {selectedEmployee.lastname}</strong>
                            </div>
                        </div>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <button
                        className="modern-button secondary"
                        onClick={handleClose}
                        disabled={isLoading}
                    >
                        Avbryt
                    </button>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        disabled={!selectedEmployee || isLoading}
                    >
                        {isLoading ? (
                            <>
                                <div className="loading-spinner" />
                                Kopplar...
                            </>
                        ) : (
                            <>
                                <Link className="me-2" size={16} />
                                Koppla
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default LinkEmployeeModal;
